import { useState, useRef, useCallback, useEffect } from 'react';
import { DRAWER_CONFIG, isIPad } from '@/constants';

export const useDrawerResize = (initialWidth?: number) => {
  // Use iPad-specific width if on iPad, otherwise use provided or default width
  const getInitialWidth = () => {
    if (initialWidth !== undefined) return initialWidth;
    return isIPad() ? DRAWER_CONFIG.IPAD_WIDTH : DRAWER_CONFIG.DEFAULT_WIDTH;
  };
  const calculatedInitialWidth = getInitialWidth();
  const [drawerWidth, setDrawerWidth] = useState(calculatedInitialWidth);
  const [prevDrawerWidth, setPrevDrawerWidth] = useState(calculatedInitialWidth);
  const [isResizingLeft, setIsResizingLeft] = useState(false);

  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerWidthRef = useRef<number>(drawerWidth);
  const startResizeXRef = useRef<number>(0);

  const handleLeftMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      document.body.style.cursor = 'col-resize';
      setIsResizingLeft(true);
      startResizeXRef.current = e.clientX;
      drawerWidthRef.current = drawerWidth;
    },
    [drawerWidth]
  );

  const handleLeftMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizingLeft) return;

      // Calculate the new width based on drag delta
      const delta = e.clientX - startResizeXRef.current;
      const newWidth = Math.max(
        180,
        Math.min(600, drawerWidthRef.current + delta)
      );

      // Update the DOM directly for immediate visual feedback
      if (drawerRef.current) {
        drawerRef.current.style.width = `${newWidth}px`;
      }
    },
    [isResizingLeft]
  );

  const handleLeftMouseUp = useCallback(() => {
    if (!isResizingLeft) return;

    document.body.style.cursor = 'default';
    setIsResizingLeft(false);

    // Get final width and update state only once
    if (drawerRef.current) {
      const finalWidth = parseInt(
        drawerRef.current.style.width || drawerWidth.toString(),
        10
      );
      setDrawerWidth(finalWidth);
      setPrevDrawerWidth(finalWidth);
      drawerWidthRef.current = finalWidth;
    }
  }, [drawerWidth, isResizingLeft]);

  // Set up event listeners for resize
  useEffect(() => {
    if (isResizingLeft) {
      window.addEventListener('mousemove', handleLeftMouseMove);
      window.addEventListener('mouseup', handleLeftMouseUp);
      document.body.classList.add('resizing');
    } else {
      window.removeEventListener('mousemove', handleLeftMouseMove);
      window.removeEventListener('mouseup', handleLeftMouseUp);
      document.body.classList.remove('resizing');
    }

    return () => {
      window.removeEventListener('mousemove', handleLeftMouseMove);
      window.removeEventListener('mouseup', handleLeftMouseUp);
      document.body.classList.remove('resizing');
    };
  }, [isResizingLeft, handleLeftMouseMove, handleLeftMouseUp]);

  // Update drawer width ref when state changes
  useEffect(() => {
    drawerWidthRef.current = drawerWidth;
  }, [drawerWidth]);

  return {
    drawerRef,
    drawerWidth,
    prevDrawerWidth,
    isResizingLeft,
    setDrawerWidth,
    setPrevDrawerWidth,
    handleLeftMouseDown
  };
};
